import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CompositeArticleImportModel } from 'src/app/models/imports/composite-article-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { CompositeArticleModel } from 'src/app/models/preliminaries/composite-article.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CompositeArticleSelectTableModel } from 'src/app/models/tables/preliminaries/composite-article-select-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class CompositeArticleService {

    private readonly _httpClient = inject(HttpClient);

    changeState(compositeArticleId: number): Observable<CompositeArticleModel> {
        const params = new HttpParams().set('compositeArticleId', compositeArticleId);
        return this._httpClient.put<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.ChangeStateAsync}`,
            null, { params: params }
        );
    }

    createCompositeArticle(articleContractModel: CompositeArticleModel): Observable<CompositeArticleModel> {
        return this._httpClient.post<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.CreateArticle}`,
            articleContractModel
        );
    }

    deleteCompositeArticle(compositeArticleId: number): Observable<CompositeArticleModel> {
        const params = new HttpParams().set('compositeArticleId', compositeArticleId);
        return this._httpClient.delete<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.DeleteArticle}`,
            { params: params }
        );
    }

    downloadGuideImport(): Observable<HttpEvent<Blob>> {
        return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.DownloadGuideImport}`,
            null, { reportProgress: true, responseType: 'blob' })
        );
    }

    exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
        const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
        return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.ExportGeneralConsultation}`,
            filters, { params, responseType: 'blob' }
        );
    }

    exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
        return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.ExportImportErrors}`, alerts,
            { responseType: 'blob' }
        );
    }

    getAllCompositeArticlesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
        const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
        return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetAllCompositeArticlesWithPagination}`,
            filters, { params: params }
        );
    }

    getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
        return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetColumnsForGeneralConsultation}`);
    }

    getColumnsCompositeArticleSelection(): Observable<ColumnResponseModel[]> {
        return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetColumnsCompositeArticleSelectionAsync}`);
    }

    getCompositeArticleById(compositeArticleId: number): Observable<CompositeArticleModel> {
        const params = new HttpParams().set('compositeArticleId', compositeArticleId);
        return this._httpClient.get<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetByIdAsync}`,
            { params: params }
        );
    }

    getCompositeArticleByKeys(code: number): Observable<CompositeArticleModel> {
        const params = new HttpParams().set('code', code);
        return this._httpClient.get<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetCompositeArticleByKeysAsync}`,
            { params: params }
        );
    }

    getCompositeArticlesForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<CompositeArticleSelectTableModel>> {
        const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant)
        return this._httpClient.post<PagedResponseModel<CompositeArticleSelectTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetCompositeArticlesForSelectionAsync}`,
            filters, { params: params }
        );
    }

    getFirstCompositeArticleByKeys(): Observable<CompositeArticleModel> {
        return this._httpClient.get<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetFirstCompositeArticleByKeysAsync}`);
    }

    getLastCompositeArticleByKeys(): Observable<CompositeArticleModel> {
        return this._httpClient.get<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetLastCompositeArticleByKeysAsync}`);
    }

    getPreviusCompositeArticleByKeys(code: number): Observable<CompositeArticleModel> {
        const params = new HttpParams().set('code', code);
        return this._httpClient.get<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetPreviusCompositeArticleByKeysAsync}`,
            { params: params }
        );
    }

    getNextCompositeArticleByKeys(code: number): Observable<CompositeArticleModel> {
        const params = new HttpParams().set('code', code);
        return this._httpClient.get<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.GetNextCompositeArticleByKeysAsync}`,
            { params: params }
        );
    }

    importCompositesArticles(compositeArticleImports: Array<CompositeArticleImportModel>): Observable<LoadPreInvoicingModel> {
        return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.ImportCompositesArticlesAsync}`,
            compositeArticleImports
        );
    }

    updateCompositeArticle(compositeArticleContractModel: CompositeArticleModel): Observable<CompositeArticleModel> {
        return this._httpClient.put<CompositeArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticle.UpdateArticle}`,
            compositeArticleContractModel
        );
    }
}